/**
 * Share an AI report with users, user groups, and/or role/level buckets.
 * Replace-all semantics: whatever is checked when you Save becomes the full
 * audience. A shared report appears in each recipient's Reports list.
 *
 * Target is either a one-shot run or a recurring schedule (targetType).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Alert } from '@/components/ui/Alert';
import { PersonMultiSelect, AudienceBucketCheckbox } from '@/components/reports/AudienceControls';
import { customReportsApi } from '@/services/customReports.service';
import { aiReportsApi, type ShareTargetType } from '@/services/aiReports.service';
import { getErrorMessage } from '@/services/apiClient';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { invalidateMyReports } from '@/hooks/useMyReports';

const BUCKETS: Array<{ key: string; label: string }> = [
  { key: 'coordinators', label: 'Coordinators' },
  { key: 'doctors', label: 'Doctors' },
  { key: 'nurses', label: 'Nurses' },
  { key: 'userlevel1', label: 'User level 1' },
  { key: 'userlevel2', label: 'User level 2' },
  { key: 'userlevel3', label: 'User level 3' },
  { key: 'userlevel4', label: 'User level 4' },
];

export function AiReportShareModal({
  isOpen,
  onClose,
  targetType,
  targetId,
  targetTitle,
}: {
  isOpen: boolean;
  onClose: () => void;
  targetType: ShareTargetType;
  targetId: number;
  targetTitle?: string;
}) {
  const queryClient = useQueryClient();
  const { titles: roleTitles } = useRoleTitles();
  const [userIds, setUserIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [buckets, setBuckets] = useState<Record<string, boolean>>({});

  const catalog = useQuery({
    queryKey: ['reports', 'catalog', 'audience'],
    queryFn: customReportsApi.catalog.audience,
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });

  const shares = useQuery({
    queryKey: ['ai-report-shares', targetType, targetId],
    queryFn: () => aiReportsApi.shares.get(targetType, targetId),
    enabled: isOpen && targetId > 0,
  });

  // Seed the controls from the current shares once loaded.
  useEffect(() => {
    if (!shares.data) return;
    setUserIds(shares.data.userIds.map(String));
    setGroupIds(shares.data.groupIds.map(String));
    setBuckets(Object.fromEntries(shares.data.buckets.map((b) => [b, true])));
  }, [shares.data]);

  const save = useMutation({
    mutationFn: () =>
      aiReportsApi.shares.put(targetType, targetId, {
        userIds: userIds.map(Number).filter((n) => n > 0),
        groupIds: groupIds.map(Number).filter((n) => n > 0),
        buckets: BUCKETS.filter((b) => buckets[b.key]).map((b) => b.key),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-report-shares', targetType, targetId] });
      invalidateMyReports(queryClient);
      onClose();
    },
  });

  const loading = catalog.isLoading || shares.isLoading;
  const groups = catalog.data?.groups ?? [];
  const users = catalog.data?.users ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={targetTitle ? `Share "${targetTitle}"` : 'Share report'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={loading}>
            Save sharing
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Spinner size="sm" /> Loading…</div>
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-slate-600">
            Anyone you share with will see this report in their own Reports list. Clearing everyone
            makes it private to you again.
          </p>

          {save.isError && (
            <Alert variant="error">{getErrorMessage(save.error, 'Failed to save sharing.')}</Alert>
          )}

          <PersonMultiSelect
            label="Share with specific users"
            people={users}
            value={userIds}
            onChange={setUserIds}
          />

          <PersonMultiSelect
            label="Share with user groups"
            people={groups}
            value={groupIds}
            onChange={setGroupIds}
            emptyLabel="No user groups configured"
          />

          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <div className="mb-2 text-sm font-medium text-slate-900">Share with staff roles</div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {BUCKETS.map((b) => (
                <AudienceBucketCheckbox
                  key={b.key}
                  // Staff-role buckets show the account's own wording; the
                  // stored bucket key is unchanged. `titles` is keyed by the
                  // same plural strings, so user-level buckets fall through.
                  label={roleTitles[b.key] ?? b.label}
                  checked={!!buckets[b.key]}
                  onChange={(v) => setBuckets((prev) => ({ ...prev, [b.key]: v }))}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
